import { Document } from '@react-pdf/renderer';

import Module from '@/client/features/issue/pdf/Module';
import CustomPage from '@/client/features/issue/pdf/Page';
import RelatedTickets from '@/client/features/issue/pdf/RelatedTickets';
import PDFMarkdown from '@/client/features/issue/pdf/Markdown';
import ReportFooter from '@/client/features/issue/pdf/ReportFooter';

import { getValues } from '@/utils';
import { formatIsoDate } from '@/utils/date';
import { useIssueState } from '../hooks/useIssueState';
import { upperFirst } from '@mantine/hooks';
import { redmineEntityToArr } from '../../issue/utils/pdf';
import { INCIDENTS_ENDPOINTS } from '../services/keys';
import { MERCURY_URL } from '@/constants/env';

const IncidentReport = ({ data }: any) => {
  const [currentStatus] = useIssueState(
    data.client_state?.[0],
    data.rfis?.filter((rfi: any) => rfi.name.includes('Takedown Request')).length > 0
  );

  return (
    <Document>
      <CustomPage
        data={{
          url: `${MERCURY_URL}/incident/${data.id}`,
          title: data.name,
          id: data.id,
          published_at: data.published_at,
          type: 'Incident Report',
        }}
        metadata={{
          extra: [
            { label: 'Category', value: data.category },
            { label: 'Priority', value: data.priority ? upperFirst(data.priority) : undefined },
            { label: 'Status', value: currentStatus ? upperFirst(currentStatus) : undefined },
          ],
        }}
      >
        <Module
          title="Overview"
          content={[
            [
              ['Created At', formatIsoDate(data.created_at, true)],
              ['Updated At', formatIsoDate(data.updated_at, true)],
              ['Status', upperFirst(currentStatus)],
              ['Priority', upperFirst(data.priority)],
              ['Assignee', getValues(data.client_state?.[0]?.assignee ?? {})[0]],
              ['Category', data.category],
              ['Subcategory', data.subcategory],
              ['Source', redmineEntityToArr(data.sources).join(', ')],
              ['Source DTG', formatIsoDate(data.published_at, true)],
            ],
          ]}
          grid
        />

        <Module
          title="General Information"
          content={[
            [
              [
                'Description',
                <PDFMarkdown
                  text={data.description}
                  footnoteClass=""
                  issue={data.id}
                  resource={INCIDENTS_ENDPOINTS.incidents}
                />,
              ],
            ],
          ]}
        />

        <RelatedTickets
          tickets={[
            ['Raw Intel', data.raw_intel],
            ['Finished Intel', data.finished_intel],
            ['Incidents', data.incidents],
            ['Takedowns', data.takedowns],
            ['RFIs', data.rfis],
          ]}
        />

        <Module
          title="History"
          content={[
            [
              [
                '',
                data.client_state?.[0]?.activities
                  ?.sort(
                    (a: any, b: any) =>
                      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                  )
                  .map(
                    (ac: any) =>
                      `${upperFirst(ac.type)} by ${getValues(ac.author ?? {})[0]} on ${formatIsoDate(ac.created_at, true)}`
                  ) ?? [],
              ],
            ],
          ]}
        />

        {data.iocs?.length > 0 && <ReportFooter iocs={data.iocs} />}
      </CustomPage>
    </Document>
  );
};

export default IncidentReport;
